import React from 'react';

interface VoucherDetails {
  voucherId?: number;
  saralCode: string;
  issueDate: string;
  activateDate: string;
  expiryDate: string;
  revokeDate: string;
  status: string;
  processor: string;
  processorBrand: string;
  bankReferenceCode: string;
  bankReferenceUuid: string;
  ownerMobile: string;
  ownerName: string;
  issueAmount: number;
  balanceAmount: number;
  currency: string;
  programCode: string;
  programName: string;
}

/** Map API PII keys to display values (no persistence; display only) */
function getDisplayFromPii(pii: Record<string, unknown>): { name: string; mobile: string } {
  const name =
    (pii.beneficiaryName as string) ??
    (pii.receiverName as string) ??
    '';
  const mobile =
    (pii.beneficiaryPhone as string) ??
    (pii.receiverPhone as string) ??
    '';
  return { name, mobile };
}

interface ERupiVoucherDetailsCardProps {
  voucherDetails: VoucherDetails;
  onRevokeClick: () => void;
  onRequestPiiAccess?: () => void;
  unmaskedPii?: Record<string, unknown> | null;
  onClearUnmaskedPii?: () => void;
  formatAmount: (amount: number | string | null | undefined) => string;
  currencySymbol: string;
}

const ERupiVoucherDetailsCard: React.FC<ERupiVoucherDetailsCardProps> = ({
  voucherDetails,
  onRevokeClick,
  onRequestPiiAccess,
  unmaskedPii,
  onClearUnmaskedPii,
  formatAmount,
  currencySymbol,
}) => {
  const getStatusColor = (status: string) => {
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'activated' || lowerStatus === 'used'
      ? 'text-green-600'
      : 'text-yellow-600';
  };

  const revokeAllowedStatus = ['used', 'activated', 'issued', 'exhausted'].includes(voucherDetails.status?.toLowerCase());
  const isIkeaVoucher = voucherDetails.programCode?.toUpperCase() === 'IKEA';
  const showRevokeButton = revokeAllowedStatus && !isIkeaVoucher;
  const displayFromPii = unmaskedPii ? getDisplayFromPii(unmaskedPii) : null;

  const isCbdc = voucherDetails.processorBrand === 'ICICI_CBDC';
  const isMyp = voucherDetails.processorBrand === 'MYP';
  const detailsHeading = isMyp ? 'MYP Voucher Details' : isCbdc ? 'Digital Rupee Details' : 'eRupi Code Details';
  const referenceCodeLabel = isMyp ? 'MYP Voucher Code:' : isCbdc ? 'Disbursement Reference ID:' : 'eRupi UMN:';
  const referenceUuidLabel = isMyp ? 'MYP Voucher ID:' : isCbdc ? 'Rule ID:' : 'eRupi UUID:';

  return (
    <div className="p-6 md:p-8 border-b border-gray-200">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{detailsHeading}</h2>
        <div className="flex items-center gap-2">
          {onRequestPiiAccess && !unmaskedPii && (
            <button
              type="button"
              onClick={onRequestPiiAccess}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Request PII Access
            </button>
          )}
          {unmaskedPii && onClearUnmaskedPii && (
            <button
              type="button"
              onClick={onClearUnmaskedPii}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
            >
              Hide unmasked data
            </button>
          )}
          {showRevokeButton && (
            <button
              onClick={onRevokeClick}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            >
              Revoke Voucher
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Saral Code:</span>
            <span className="text-sm font-semibold text-gray-900">{voucherDetails.saralCode}</span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Issue Date:</span>
            <span className="text-sm font-semibold text-gray-900">{voucherDetails.issueDate}</span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Activate Date:</span>
            <span className="text-sm font-semibold text-gray-900">{voucherDetails.activateDate}</span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Expiry Date:</span>
            <span className="text-sm font-semibold text-gray-900">{voucherDetails.expiryDate}</span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Revoke Date:</span>
            <span className="text-sm font-semibold text-gray-900">{voucherDetails.revokeDate}</span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Status:</span>
            <span className={`text-sm font-semibold ${getStatusColor(voucherDetails.status)}`}>
              {voucherDetails.status}
            </span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Processor:</span>
            <span className="text-sm font-semibold text-gray-900">{voucherDetails.processor}</span>
          </div>
        </div>

        {/* Full-width fields for long values - positioned after Processor */}
        <div className="flex flex-col py-2 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-600 mb-2">{referenceCodeLabel}</span>
          <span className="text-sm font-semibold text-gray-900 font-mono break-all">
            {voucherDetails.bankReferenceCode || '-'}
          </span>
        </div>

        <div className="flex flex-col py-2 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-600 mb-2">{referenceUuidLabel}</span>
          <span className="text-sm font-semibold text-gray-900 font-mono break-all">
            {voucherDetails.bankReferenceUuid || '-'}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Owner Mobile:</span>
            <span className="text-sm font-semibold text-gray-900">
              {displayFromPii?.mobile ?? voucherDetails.ownerMobile ?? '-'}
            </span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Owner Name:</span>
            <span className="text-sm font-semibold text-gray-900">
              {displayFromPii?.name ?? voucherDetails.ownerName ?? '-'}
            </span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Issue Amount:</span>
            <span className="text-sm font-semibold text-gray-900">
              {currencySymbol}{formatAmount(voucherDetails.issueAmount)}
            </span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Balance Amount:</span>
            <span className="text-sm font-semibold text-gray-900">
              {currencySymbol}{formatAmount(voucherDetails.balanceAmount)}
            </span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Program Code:</span>
            <span className="text-sm font-semibold text-gray-900">{voucherDetails.programCode || '-'}</span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Program Name:</span>
            <span className="text-sm font-semibold text-gray-900">{voucherDetails.programName}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERupiVoucherDetailsCard;

